import axios from 'axios';
import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, {
  Dispatch,
  ChangeEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { QueryCache, useMutation, useQuery, useQueryClient } from 'react-query';
import clsx from 'clsx';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import SearchIcon from '@mui/icons-material/Search';
import { xapi } from '../../hooks/session';
import { SwSnackbar } from '../commonComponent/views/SwSnackbar';
import SwLoading from '../commonComponent/spinner/SwLoading';
import { SwPagination } from '../commonComponent/tableElement/SwPagination';
import {
  commaFormat,
  encodeSearchWord,
  getGender,
  getMonthStartEndText,
  getPostFixByType,
  timestamp2Localestring,
} from '../../common/helper';
import {
  KEY_BOOK_GROUP_LIST,
  KEY_BOOK_INFO_LIST,
  KEY_CLASS_READ_RANK_LIST,
  KEY_READ_TEXT,
  KEY_WALK_TEXT,
  KEY_WRITE_TEXT,
} from '../../common/key';
import excellog from '../../images/sub/excel_icon.svg';
import {
  getBookGroupList,
  getBookInfoList,
  registerBookGroup,
} from '../../common/api/ApiBook';
import { SwBookGroup } from '../commonComponent/SwBookGroup';
import { SwBookList } from '../commonComponent/SwBookList';
import { uploadFile } from '../../common/api/ApiUpload';
import { RESULT_OK } from '../../common/resultCode';
import { SwClassStatTab } from '../commonComponent/SwClassStatTab';
import { SwUserStatDetail } from '../commonComponent/SwUserStatDetail';
import { getClassReadRankList } from '../../common/api/ApiClassStat';
import { SwUserRankDetail } from '../commonComponent/SwUserRankDetail';
import { SwRankDropbox } from '../commonComponent/SwRankDropbox';

const readColumns = [
  '순위',
  '닉네임',
  '출석번호',
  '성별',
  '완독 권수',
  '금',
  '은',
  '동',
  '상세 보기',
];

const stepColumns = [
  '순위',
  '닉네임',
  '출석번호',
  '성별',
  '누적 걸음수',
  '평균 걸음수',
  '금',
  '은',
  '동',
  '상세 보기',
];

const writeColumns = [
  '순위',
  '닉네임',
  '출석번호',
  '성별',
  '작성글 수',
  '금',
  '은',
  '동',
  '상세 보기',
];

interface Props {
  title: string;
  targetMonth: any;
  data: any;
  onDownload: (orderType: number) => void;
}

export const ClassRankTable: React.FC<Props> = ({
  title,
  targetMonth,
  data,
  onDownload,
}) => {
  const [loading, setLoading] = useState<any>(false);
  const [downloadType, setDownloadType] = useState<any>('');
  const [dialogMessage, setDialogMessage] = useState<string>('');
  const [selectedSeq, setSelectedSeq] = useState<number>(-1);
  const [selectItem, setSelectItem] = useState<number>(1);
  const [sortedData, setSortedData] = useState<any[]>([]);
  const rowsPerPageOptions = [5, 10, 15, 20];
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [openSchoolClass, setOpenSchoolClass] = useState<boolean>(false);
  const [openUserRank, setOpenUserRank] = useState<boolean>(false);

  const detailInfo = (idx: number) => {
    setSelectedSeq(sortedData[idx].memberSeq);
    setOpenUserRank(true);
  };

  const handleChangePage = (_event: any, newPage: number) => {
    setPage(newPage);
  };

  useEffect(() => {
    const newData =
      title === KEY_READ_TEXT && selectItem === 1
        ? data?.rankList.slice().sort((a: any, b: any) => {
            if (b.finishCount !== a.finishCount) {
              return b.finishCount - a.finishCount;
            }
            return a.memberNumber - b.memberNumber; // 이차 정렬
          })
        : title === KEY_READ_TEXT && selectItem === 2
        ? data?.rankList.slice().sort((a: any, b: any) => {
            if (b.goldCount !== a.goldCount) {
              return b.goldCount - a.goldCount;
            }
            if (b.finishCount !== a.finishCount) {
              return b.finishCount - a.finishCount;
            }
            return a.memberNumber - b.memberNumber;
          })
        : title === KEY_WALK_TEXT && selectItem === 1
        ? data?.rankList.slice().sort((a: any, b: any) => {
            if (b.totalStep !== a.totalStep) {
              return b.totalStep - a.totalStep;
            }
            if (b.goldCount !== a.goldCount) {
              return b.goldCount - a.goldCount;
            }
            if (b.silverCount !== a.silverCount) {
              return b.silverCount - a.silverCount;
            }
            if (b.bronzeCount !== a.bronzeCount) {
              return b.bronzeCount - a.silverCount;
            }
            return a.memberNumber - b.memberNumber; // 이차 정렬
          })
        : title === KEY_WALK_TEXT && selectItem === 2
        ? data?.rankList.slice().sort((a: any, b: any) => {
            if (b.avgStep !== a.avgStep) {
              return b.avgStep - a.avgStep;
            }
            if (b.totalStep !== a.totalStep) {
              return b.totalStep - a.totalStep;
            }
            if (b.goldCount !== a.goldCount) {
              return b.goldCount - a.goldCount;
            }
            if (b.silverCount !== a.silverCount) {
              return b.silverCount - a.silverCount;
            }
            if (b.bronzeCount !== a.bronzeCount) {
              return b.bronzeCount - a.silverCount;
            }
            return a.memberNumber - b.memberNumber; // 이차 정렬
          })
        : title === KEY_WALK_TEXT && selectItem === 3
        ? data?.rankList.slice().sort((a: any, b: any) => {
            if (b.goldCount !== a.goldCount) {
              return b.goldCount - a.goldCount;
            }
            if (b.totalStep !== a.totalStep) {
              return b.totalStep - a.totalStep;
            }
            if (b.silverCount !== a.silverCount) {
              return b.silverCount - a.silverCount;
            }
            if (b.bronzeCount !== a.bronzeCount) {
              return b.bronzeCount - a.silverCount;
            }
            return a.memberNumber - b.memberNumber; // 이차 정렬
          })
        : title === KEY_WRITE_TEXT && selectItem === 1
        ? data?.rankList.slice().sort((a: any, b: any) => {
            if (b.writeCount !== a.writeCount) {
              return b.writeCount - a.writeCount;
            }
            if (b.goldCount !== a.goldCount) {
              return b.goldCount - a.goldCount;
            }
            return a.memberNumber - b.memberNumber;
          })
        : title === KEY_WRITE_TEXT && selectItem === 2
        ? data?.rankList.slice().sort((a: any, b: any) => {
            if (b.goldCount !== a.goldCount) {
              return b.goldCount - a.goldCount;
            }
            if (b.writeCount !== a.writeCount) {
              return b.writeCount - a.writeCount;
            }
            return a.memberNumber - b.memberNumber;
          })
        : [];
    setPage(1);
    setSortedData(newData);
  }, [data, title, selectItem]);

  useEffect(() => {
    data && setTotalRecords(data.totalRecordCount);
  }, []);

  return (
    <>
      <Paper sx={{ p: '1.5rem' }}>
        <Typography variant='h4'>{title}</Typography>
        <Box className='flex_between'>
          <Box>
            {data === undefined ||
            data === null ||
            data.medalInfo === undefined ? (
              <Typography className='title'>달성기준 </Typography>
            ) : (
              <Typography className='title'>
                달성기준({title === KEY_READ_TEXT ? '월' : '일'}) 금{' '}
                <span style={{ color: '#2581FF' }}>
                  {data.medalInfo.goldLevel}
                </span>
                {getPostFixByType(title)} 은{' '}
                <span style={{ color: '#2581FF' }}>
                  {data.medalInfo.silverLevel}
                </span>
                {getPostFixByType(title)} 동{' '}
                <span style={{ color: '#2581FF' }}>
                  {data.medalInfo.bronzeLevel}
                </span>
                {getPostFixByType(title)}
              </Typography>
            )}
          </Box>
          <Box className='flex_start'>
            <Typography>
              기준 : {getMonthStartEndText(targetMonth * 1000)}
            </Typography>
            <Button
              sx={{ ml: '1rem' }}
              color='info'
              variant='outlined'
              onClick={() => onDownload(selectItem)}
            >
              데이터 다운로드
              <FileDownloadRoundedIcon color='success' />
            </Button>
          </Box>
        </Box>
        <Box>
          <SwRankDropbox title={title} setSelectItem={setSelectItem} />
        </Box>
        <Paper sx={{ p: '1rem' }}>
          <TableContainer sx={{ height: '67vh', overflowY: 'scroll' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {(title === KEY_READ_TEXT
                    ? readColumns
                    : title === KEY_WALK_TEXT
                    ? stepColumns
                    : writeColumns
                  ).map((column: any, idx: number) => (
                    <TableCell key={idx.toString()} sx={{ p: '.4rem .7rem' }}>
                      {column}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData &&
                  sortedData.length > 0 &&
                  sortedData
                    ?.slice((page - 1) * rowsPerPage, page * rowsPerPage)
                    .map((row: any, idx: number) =>
                      title === KEY_READ_TEXT
                        ? [
                            row.rankOrder,
                            row.nickname,
                            row.memberNumber,
                            getGender(row.gender),
                            row.finishCount,
                            commaFormat(row.goldCount),
                            commaFormat(row.silverCount),
                            commaFormat(row.bronzeCount),
                          ]
                        : title === KEY_WALK_TEXT
                        ? [
                            row.rankOrder,
                            row.nickname,
                            row.memberNumber,
                            getGender(row.gender),
                            commaFormat(row.totalStep),
                            commaFormat(Math.round(row.avgStep)),
                            commaFormat(row.goldCount),
                            commaFormat(row.silverCount),
                            commaFormat(row.bronzeCount),
                          ]
                        : [
                            row.rankOrder,
                            row.nickname,
                            row.memberNumber,
                            getGender(row.gender),
                            row.writeCount,
                            commaFormat(row.goldCount),
                            commaFormat(row.silverCount),
                            commaFormat(row.bronzeCount),
                          ]
                    )
                    .map((row: any, idx: number) => (
                      <TableRow key={idx.toString()}>
                        {(title === KEY_READ_TEXT
                          ? readColumns
                          : title === KEY_WALK_TEXT
                          ? stepColumns
                          : writeColumns
                        ).map((column: any, i: number) =>
                          i ===
                          (title === KEY_READ_TEXT
                            ? readColumns
                            : title === KEY_WALK_TEXT
                            ? stepColumns
                            : writeColumns
                          ).length -
                            1 ? (
                            <TableCell
                              key={`tbl_${title}_${i}`}
                              sx={{ p: '.7rem' }}
                            >
                              <Button
                                variant='outlined'
                                color='info'
                                onClick={() => detailInfo(idx)}
                              >
                                보기
                              </Button>
                            </TableCell>
                          ) : (
                            <TableCell
                              key={`tbl_${title}_${i}`}
                              sx={{ p: '.7rem' }}
                            >
                              {row[i]}
                            </TableCell>
                          )
                        )}
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
          <SwPagination
            page={page}
            handleChangePage={handleChangePage}
            count={
              data &&
              data.rankList &&
              Math.ceil(data.rankList.length / rowsPerPage)
            }
          />
        </Paper>
      </Paper>
      <SwSnackbar
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        contents={dialogMessage}
      />
      <SwUserRankDetail
        title={title}
        memberSeq={selectedSeq}
        targetMonth={targetMonth}
        medalInfo={data?.medalInfo}
        open={openUserRank}
        onClose={() => setOpenUserRank(false)}
      />
      {loading && <SwLoading />}
    </>
  );
};
